import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { APPROVAL_THRESHOLD_MINUTES, MEETING_KIND, MEETING_LIMITS, TIME_ENTRY_LIMITS, requiresApproval, type ActivityType, type WorkActivity } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import {
  fetchActivityCatalog,
  fetchPeople,
  fetchProjects,
  logTimeEntry,
  timeEntryErrorCode,
  timeEntryErrorMessage,
  type LogTimeEntryResult,
  type WorkHoursPerson,
  type WorkHoursProjectOption,
} from "../services/workHoursService";
import { today } from "../lib/heatmap";
import { activityTypeName } from "../lib/kindLabel";
import { buildThemeClasses, inputClass } from "../theme";
import ActivityPicker from "./ActivityPicker";

// ---------------------------------------------------------------------------
// Entrada manual (#1060): reuniones, cursos, soporte, tiempo facturable… lo que
// no se puede ligar a un issue o PR de GitHub. TODA entrada queda ligada a un
// repositorio: se elige de un dropdown con los repos del proyecto y los repos
// con actividad reciente que caen en él (#1138); «Otro…» permite escribir
// `owner/nombre` cuando el repo todavía no aparece.
//
// #1300 / #1306: el «Tipo» sale del catálogo de actividades de la compañía y
// solo ofrece lo que el PUESTO de la persona puede registrar a mano (ventas no
// ve «Desarrollo»; marketing no ve «Ticket de soporte»…). El servidor vuelve a
// comprobarlo (`KIND_NOT_ALLOWED`).
// #1303: toda entrada manual va ligada a un ticket (`activityId`): se busca uno
// del sprint o del backlog, o se crea al vuelo con el mismo tipo.
// #1404: una REUNIÓN dice cuándo fue, de qué hora a qué hora y con quiénes
// (gente del equipo + «alguien más»). La duración sale del horario y el
// servidor registra la misma entrada a cada asistente del equipo.
// ---------------------------------------------------------------------------

const REPO_RE = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;
/** Valor del dropdown para escribir un repo que no está en la lista. */
const OTHER_REPO = "__other__";
/** Minutos desde medianoche de un `HH:mm`, o `null`. */
const toMinutes = (hhmm: string): number | null => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
/** Códigos del contrato con texto propio en la UI; lo demás muestra el mensaje del servidor. */
const ERROR_KEYS: Record<string, string> = {
  ACTIVITY_REQUIRED: "workHours.timeEntries.errors.ACTIVITY_REQUIRED",
  ACTIVITY_NOT_FOUND: "workHours.timeEntries.errors.ACTIVITY_NOT_FOUND",
  KIND_NOT_ALLOWED: "workHours.timeEntries.errors.KIND_NOT_ALLOWED",
};

interface Props {
  isDarkMode: boolean;
  onClose: () => void;
  /** Se llama tras guardar, para recargar la lista. Trae la respuesta: `approval: "pending"` ⇒ espera al tech lead (#1281). */
  onSaved: (result: LogTimeEntryResult) => void;
}

export default function ManualEntryModal({ isDarkMode, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation("common");
  const tc = buildThemeClasses(isDarkMode);
  const [projects, setProjects] = useState<WorkHoursProjectOption[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Catálogo de actividades: `null` mientras carga; `manualKeys` = lo que mi puesto registra a mano.
  const [catalog, setCatalog] = useState<ActivityType[] | null>(null);
  const [manualKeys, setManualKeys] = useState<string[] | null>(null);
  const [catalogError, setCatalogError] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [repo, setRepo] = useState("");
  const [otherRepo, setOtherRepo] = useState(false);
  const [date, setDate] = useState(today());
  const [hours, setHours] = useState("1");
  const [kind, setKind] = useState("");
  const [activity, setActivity] = useState<WorkActivity | null>(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  // #1404: reunión — horario y asistentes.
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [people, setPeople] = useState<WorkHoursPerson[]>([]);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [others, setOthers] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects("all")
      .then((list) => {
        setProjects(list);
        if (list.length) setProjectId(list[0].id);
      })
      .catch(() => setLoadError(true));
    fetchActivityCatalog()
      .then((res) => {
        setCatalog(res.data);
        setManualKeys(res.meta.manualKeys);
      })
      .catch(() => {
        setCatalogError(true);
        setManualKeys([]);
      });
    // Gente del equipo para los asistentes; si falla, queda «alguien más».
    fetchPeople()
      .then((list) => setPeople(list.filter((p) => !p.isMe)))
      .catch(() => setPeople([]));
  }, []);

  // Tipos que ofrece el formulario: los del catálogo que mi puesto puede
  // registrar a mano, en el orden del catálogo. «Reunión» (o el primero) por omisión.
  const kindOptions = useMemo(() => {
    if (!catalog || !manualKeys) return [];
    return catalog.filter((x) => manualKeys.includes(x.key));
  }, [catalog, manualKeys]);
  useEffect(() => {
    if (kind || !kindOptions.length) return;
    setKind(kindOptions.some((x) => x.key === "meeting") ? "meeting" : kindOptions[0].key);
  }, [kind, kindOptions]);

  const project = useMemo(() => projects?.find((p) => p.id === projectId) ?? null, [projects, projectId]);
  const exactRepos = useMemo(() => (project?.repos ?? []).filter((r) => !r.endsWith("/*")), [project]);
  // Opciones del dropdown: los repos exactos del proyecto + los que tuvieron
  // actividad y caen en él (comodín `owner/*` o proyecto sin repos, #1138).
  const repoOptions = useMemo(
    () => [...new Set([...exactRepos, ...(project?.knownRepos ?? [])])].sort((a, b) => a.localeCompare(b)),
    [exactRepos, project],
  );
  // ¿Se puede escribir un repo que no está en la lista? Con comodín o sin repos, sí.
  const allowsOther = !project || exactRepos.length === 0 || (project.repos ?? []).some((r) => r.endsWith("/*"));

  useEffect(() => {
    setOtherRepo(repoOptions.length === 0);
    setRepo(repoOptions.length === 1 ? repoOptions[0] : "");
  }, [projectId, repoOptions]);

  const isMeeting = kind === MEETING_KIND;
  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);
  const meetingMinutes = startMin !== null && endMin !== null && endMin > startMin ? endMin - startMin : null;
  // En una reunión la duración sale del horario; en lo demás, del campo «Horas».
  const minutes = isMeeting ? meetingMinutes ?? NaN : Math.round(Number(hours) * 60);
  const pickedPeople = people.filter((p) => attendees.includes(p.teamMemberId));
  const toggleAttendee = (id: string) => setAttendees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const needsReason = Boolean(project && project.assigned === false);
  // #1281: una entrada manual de más de 1 h espera la aprobación del tech lead.
  const needsApproval = requiresApproval("ui", minutes);
  const catalogLoading = manualKeys === null;
  const noManualKinds = manualKeys !== null && kindOptions.length === 0;

  const submit = async () => {
    setError(null);
    if (!project) return setError(t("workHours.timeEntries.add.errors.project"));
    if (!kind) return setError(t("workHours.timeEntries.add.errors.kind"));
    if (!activity) return setError(t("workHours.timeEntries.add.errors.activity"));
    if (!REPO_RE.test(repo.trim())) return setError(t("workHours.timeEntries.add.errors.repo"));
    if (isMeeting && meetingMinutes === null) return setError(t("workHours.timeEntries.add.errors.meetingTime"));
    if (isMeeting && attendees.length === 0 && !others.trim()) return setError(t("workHours.timeEntries.add.errors.meetingAttendees"));
    if (!Number.isFinite(minutes) || minutes < TIME_ENTRY_LIMITS.MIN_MINUTES || minutes > TIME_ENTRY_LIMITS.MAX_MINUTES) {
      return setError(t("workHours.timeEntries.add.errors.duration"));
    }
    if (note.trim().length < TIME_ENTRY_LIMITS.NOTE_MIN) return setError(t("workHours.timeEntries.add.errors.note"));
    if (needsReason && reason.trim().length < 10) return setError(t("workHours.timeEntries.add.errors.reason"));
    setSaving(true);
    try {
      const result = await logTimeEntry({
        projectId: project.id,
        date,
        minutes,
        note: note.trim(),
        kind,
        repo: repo.trim(),
        activityId: activity.id,
        unassignedReason: needsReason ? reason.trim() : undefined,
        ...(isMeeting ? { start: startTime, end: endTime, meeting: { attendeeTeamMemberIds: attendees, otherAttendees: others.trim() || null } } : {}),
      });
      setSaving(false);
      onSaved(result);
    } catch (err) {
      setSaving(false);
      const code = timeEntryErrorCode(err);
      const key = code ? ERROR_KEYS[code] : undefined;
      setError(key ? t(key, { message: timeEntryErrorMessage(err) ?? "" }) : timeEntryErrorMessage(err) ?? t("workHours.timeEntries.add.errors.save"));
    }
  };

  const label = `block text-xs font-medium ${tc.textSecondary}`;
  const field = `mt-1 w-full ${inputClass(isDarkMode)}`;

  return (
    <Modal
      title={t("workHours.timeEntries.add.title")}
      themeClasses={tc}
      onClose={() => !saving && onClose()}
      width="max-w-lg"
      content={
        <div className="space-y-3" data-testid="manual-entry-modal">
          <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.timeEntries.add.hint")}</p>
          {loadError && <p className="text-xs text-red-500">{t("workHours.timeEntries.add.errors.projects")}</p>}
          {catalogError && <p className="text-xs text-red-500">{t("workHours.timeEntries.add.errors.catalog")}</p>}
          <label className={label}>
            {t("workHours.timeEntries.add.kind")}
            <select
              aria-label={t("workHours.timeEntries.add.kind")}
              value={kind}
              disabled={catalogLoading || noManualKinds}
              onChange={(e) => setKind(e.target.value)}
              className={field}
              data-testid="manual-entry-kind"
            >
              {catalogLoading && <option value="">{t("workHours.timeEntries.add.loadingCatalog")}</option>}
              {kindOptions.map((k) => (
                <option key={k.key} value={k.key}>
                  {activityTypeName(k, i18n?.language)}
                </option>
              ))}
            </select>
          </label>
          {noManualKinds && (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600" data-testid="manual-entry-no-kinds">
              <i className="ri-information-line mr-1" />
              {t("workHours.timeEntries.add.noManualKinds")}
            </p>
          )}
          <div className={`grid gap-3 ${isMeeting ? "grid-cols-3" : "grid-cols-2"}`}>
            <label className={label}>
              {t(isMeeting ? "workHours.timeEntries.add.meetingDate" : "workHours.timeEntries.add.date")}
              <input type="date" aria-label={t("workHours.timeEntries.add.date")} value={date} max={today()} onChange={(e) => setDate(e.target.value)} className={field} />
            </label>
            {isMeeting ? (
              <>
                <label className={label}>
                  {t("workHours.timeEntries.add.meetingFrom")}
                  <input type="time" aria-label={t("workHours.timeEntries.add.meetingFrom")} value={startTime} onChange={(e) => setStartTime(e.target.value)} className={field} data-testid="manual-entry-start" />
                </label>
                <label className={label}>
                  {t("workHours.timeEntries.add.meetingTo")}
                  <input type="time" aria-label={t("workHours.timeEntries.add.meetingTo")} value={endTime} onChange={(e) => setEndTime(e.target.value)} className={field} data-testid="manual-entry-end" />
                </label>
              </>
            ) : (
              <label className={label}>
                {t("workHours.timeEntries.add.hours")}
                <input
                  type="number"
                  aria-label={t("workHours.timeEntries.add.hours")}
                  min={0.25}
                  max={16}
                  step={0.25}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className={field}
                />
              </label>
            )}
          </div>
          {isMeeting && (
            <div className="space-y-3" data-testid="manual-entry-meeting">
              <p className={`text-[11px] ${tc.textSecondary}`} data-testid="manual-entry-meeting-duration">
                {meetingMinutes === null ? t("workHours.timeEntries.add.errors.meetingTime") : t("workHours.timeEntries.add.meetingDuration", { hours: Math.round((meetingMinutes / 60) * 100) / 100 })}
              </p>
              <div>
                <span className={label}>{t("workHours.timeEntries.add.attendees")}</span>
                <button
                  type="button"
                  onClick={() => setAttendeesOpen((v) => !v)}
                  aria-expanded={attendeesOpen}
                  className={`${field} flex items-center justify-between text-left`}
                  data-testid="manual-entry-attendees-toggle"
                >
                  <span className="truncate">{pickedPeople.length ? pickedPeople.map((p) => p.name).join(", ") : t("workHours.timeEntries.add.attendeesPlaceholder")}</span>
                  <i className={`ri-arrow-${attendeesOpen ? "up" : "down"}-s-line ml-2`} />
                </button>
                {attendeesOpen && (
                  <div className={`mt-1 max-h-40 overflow-y-auto rounded-lg border text-xs ${tc.border}`} data-testid="manual-entry-attendees-list">
                    {people.length === 0 && <p className={`px-3 py-2 ${tc.textSecondary}`}>{t("workHours.timeEntries.add.attendeesEmpty")}</p>}
                    {people.map((p) => (
                      <label key={p.teamMemberId} className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${tc.buttonHover}`}>
                        <input type="checkbox" checked={attendees.includes(p.teamMemberId)} onChange={() => toggleAttendee(p.teamMemberId)} data-testid={`manual-entry-attendee-${p.teamMemberId}`} />
                        <span className={`flex-1 truncate ${tc.text}`}>{p.name}</span>
                        {!p.hasIdentity && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-600">{t("workHours.timeEntries.add.attendeeNoIdentity")}</span>}
                      </label>
                    ))}
                  </div>
                )}
                {pickedPeople.length > 0 && <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{t("workHours.timeEntries.add.attendeesHint")}</p>}
              </div>
              <label className={label}>
                {t("workHours.timeEntries.add.otherAttendees")}
                <input
                  aria-label={t("workHours.timeEntries.add.otherAttendees")}
                  value={others}
                  maxLength={MEETING_LIMITS.OTHERS_MAX}
                  placeholder={t("workHours.timeEntries.add.otherAttendeesPlaceholder")}
                  onChange={(e) => setOthers(e.target.value)}
                  className={field}
                  data-testid="manual-entry-others"
                />
              </label>
            </div>
          )}
          <label className={label}>
            {t("workHours.timeEntries.add.project")}
            <select aria-label={t("workHours.timeEntries.add.project")} value={projectId} onChange={(e) => setProjectId(e.target.value)} className={field}>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.assigned === false ? ` · ${t("workHours.timeEntries.add.notAssigned")}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className={label}>
            {t("workHours.timeEntries.add.repo")}
            {repoOptions.length > 0 && (
              <select
                aria-label={t("workHours.timeEntries.add.repo")}
                data-testid="manual-entry-repo-select"
                value={otherRepo ? OTHER_REPO : repo}
                onChange={(e) => {
                  const v = e.target.value;
                  setOtherRepo(v === OTHER_REPO);
                  setRepo(v === OTHER_REPO ? "" : v);
                }}
                className={field}
              >
                <option value="">{t("workHours.timeEntries.add.pickRepo")}</option>
                {repoOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                {allowsOther && <option value={OTHER_REPO}>{t("workHours.timeEntries.add.otherRepo")}</option>}
              </select>
            )}
            {otherRepo && (
              <input
                aria-label={repoOptions.length > 0 ? t("workHours.timeEntries.add.otherRepo") : t("workHours.timeEntries.add.repo")}
                value={repo}
                placeholder="Coding-Cloud-Online/CC-CONSOLE"
                onChange={(e) => setRepo(e.target.value)}
                className={field}
              />
            )}
          </label>
          <div>
            <span className={label}>{t("workHours.timeEntries.add.activity")}</span>
            <ActivityPicker
              isDarkMode={isDarkMode}
              tc={tc}
              typeKey={kind}
              projectId={project?.id ?? null}
              repo={REPO_RE.test(repo.trim()) ? repo.trim() : null}
              value={activity}
              onChange={setActivity}
              disabled={!kind}
            />
          </div>
          <label className={label}>
            {t("workHours.timeEntries.add.note")}
            <textarea
              aria-label={t("workHours.timeEntries.add.note")}
              value={note}
              maxLength={TIME_ENTRY_LIMITS.NOTE_MAX}
              rows={2}
              placeholder={t("workHours.timeEntries.add.notePlaceholder")}
              onChange={(e) => setNote(e.target.value)}
              className={field}
            />
          </label>
          {needsApproval && (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600" data-testid="manual-entry-approval-warning">
              <i className="ri-hourglass-line mr-1" />
              {t("workHours.timeEntries.add.approvalWarning", { hours: APPROVAL_THRESHOLD_MINUTES / 60 })}
            </p>
          )}
          {needsReason && (
            <label className={label}>
              {t("workHours.timeEntries.add.reason")}
              <textarea aria-label={t("workHours.timeEntries.add.reason")} value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} className={field} />
            </label>
          )}
          {error && (
            <p className="text-xs text-red-500" data-testid="manual-entry-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving} className={`rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
              {t("workHours.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || !projects || noManualKinds}
              className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-60"
              data-testid="manual-entry-save"
            >
              {saving ? t("workHours.timeEntries.add.saving") : t("workHours.timeEntries.add.save")}
            </button>
          </div>
        </div>
      }
    />
  );
}
